import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import countries, { type ICountry } from 'src/shared/entities/countries';
import { Card } from '@/components/ui/card';
import { cn } from 'src/lib/utils';

const organizationFormSchema = z.object({
  companyName: z.string().trim().min(1, 'Enter the organization name'),
  street: z.string().trim().min(1, 'Enter the street address'),
  city: z.string().trim().min(1, 'Enter the city'),
  postalCode: z.string().trim().min(1, 'Enter the city'),
  country: z.string().min(1, 'Select a country'),
  streetNumber: z.string().trim().optional(),
  region: z.string().trim().optional(),
  companyWebsite: z.string().trim().optional(),
  companyRegistration: z.string().trim().optional(),
});

export type IOrganizationForm = z.infer<typeof organizationFormSchema>;

type Props = {
  onCancel?: () => void;
  onSubmit: (data: IOrganizationForm) => void;
  classes?: {
    form?: string;
    footer?: string;
    submitBtn?: string;
  };
  submitBtnText?: string | React.ReactNode;
  defaultValues?: IOrganizationForm;
  registrationTooltip?: React.ReactNode;
  withCard?: boolean;
};

const FormContent = ({
  form,
  registrationTooltip,
  className,
}: {
  form: UseFormReturn<IOrganizationForm>;
  registrationTooltip?: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-4', className)}>
    <div className="col-span-1 space-y-4">
      <FormField
        control={form.control}
        name="companyName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Organization name</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., Acme Corporation" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="companyWebsite"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2 mb-2">
              <FormLabel className="mb-0">Website</FormLabel>
              <span className="text-input-placeholder text-xs leading-none">Optional</span>
            </div>
            <FormControl>
              <Input {...field} placeholder="https://example.com" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="companyRegistration"
        render={({ field }) => (
          <FormItem className="relative">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2.5">
                <FormLabel className="mb-0">Registration number</FormLabel>
                {registrationTooltip}
              </div>
              <span className="text-input-placeholder text-xs leading-none">Optional</span>
            </div>
            <FormControl>
              <Input {...field} placeholder="e.g., 12-3456789" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
    <div className="col-span-1 space-y-4">
      <FormField
        control={form.control}
        name="street"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Street address</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., 132 Main Street" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="streetNumber"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2 mb-2">
              <FormLabel className="mb-0">Number, suite or unit</FormLabel>
              <span className="text-input-placeholder text-xs leading-none">Optional</span>
            </div>
            <FormControl>
              <Input {...field} placeholder="e.g., suite 300" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>City</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., San Francisco" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="region"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2 mb-2">
              <FormLabel className="mb-0">State, province, or region</FormLabel>
              <span className="text-input-placeholder text-xs leading-none">Optional</span>
            </div>
            <FormControl>
              <Input {...field} placeholder="e.g., California" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="postalCode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Zip or postal code</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., 94015" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Country</FormLabel>
            <Combobox
              items={countries}
              itemToStringLabel={(country: ICountry) => country.name}
              onValueChange={(country: ICountry | null) => field.onChange(country?.name ?? '')}
              defaultValue={countries.find((country) => country.name === field.value)}
            >
              <FormControl>
                <ComboboxInput placeholder="Select your country" showClear className="w-full" />
              </FormControl>
              <ComboboxContent>
                <ComboboxEmpty>No countries found</ComboboxEmpty>
                <ComboboxList>
                  {(country) => (
                    <ComboboxItem key={country.name} value={country}>
                      {country.name}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  </div>
);

const OrganizationForm = ({
  onCancel,
  onSubmit,
  classes: { footer: footerClasses = '', form: formClasses = '', submitBtn: submitBtnClasses = '' } = {},
  submitBtnText = 'Save',
  defaultValues,
  registrationTooltip,
  withCard = false,
}: Props) => {
  const form = useForm<IOrganizationForm>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {withCard ? (
          <Card className="p-6 mb-6">
            <FormContent form={form} registrationTooltip={registrationTooltip} />
          </Card>
        ) : (
          <FormContent form={form} registrationTooltip={registrationTooltip} className={formClasses} />
        )}

        <div className={cn('flex justify-between', footerClasses)}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} type="button" size="sm">
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            size="sm"
            className={submitBtnClasses}
          >
            {submitBtnText}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OrganizationForm;
